import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { FaFacebookF, FaInstagram, FaWhatsapp, FaYoutube } from 'react-icons/fa';
import TermsModal from './TermsModal';

function Footer() {
  const [showTerms, setShowTerms] = useState(false);

  return (
    <>
      {/* CTA Banner */}
      <div className="bg-gradient-to-r from-green-600 to-green-500 mx-4 md:mx-auto max-w-5xl -mb-16 relative z-10 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between shadow-xl">
        <div>
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">Book Your Court Today.</h3>
          <div className="flex flex-wrap gap-4 text-green-100 text-sm">
            <span className="flex items-center"><CheckCircleIcon className="h-4 w-4 mr-1" /> Premium Court</span>
            <span className="flex items-center"><CheckCircleIcon className="h-4 w-4 mr-1" /> Easy Booking</span>
            <span className="flex items-center"><CheckCircleIcon className="h-4 w-4 mr-1" /> All 7 Days</span>
          </div>
        </div>
        <div className="flex gap-3 mt-4 md:mt-0">
          <Link to="/booking" className="bg-white text-green-700 px-6 py-3 rounded-full font-semibold hover:bg-green-50 transition-colors text-sm whitespace-nowrap">
            Book Now →
          </Link>
          <Link to="/contact-payment" className="border border-white text-white px-6 py-3 rounded-full font-semibold hover:bg-white hover:text-green-700 transition-colors text-sm whitespace-nowrap">
            Contact Us →
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-24 pb-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            {/* Logo & About */}
            <div className="col-span-2 md:col-span-1">
              <Link to="/" className="flex items-center space-x-3 mb-4">
                <img
                  src="/images/SquareLogo.jpg"
                  alt="Best Badminton Club"
                  className="h-10 w-auto object-contain rounded"
                />
                <h3 className="text-lg font-bold">Best Badminton Club</h3>
              </Link>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                Premium badminton facility in Urapakkam, Chennai with world-class court and amenities.
              </p>
              <div className="flex space-x-3">
                <button className="bg-gray-800 hover:bg-blue-600 p-2 rounded-full transition-colors">
                  <FaFacebookF className="h-4 w-4" />
                </button>
                <button className="bg-gray-800 hover:bg-pink-600 p-2 rounded-full transition-colors">
                  <FaInstagram className="h-4 w-4" />
                </button>
                <a href="https://wa.me/918080517517" target="_blank" rel="noopener noreferrer" className="bg-gray-800 hover:bg-green-600 p-2 rounded-full transition-colors">
                  <FaWhatsapp className="h-4 w-4" />
                </a>
                <button className="bg-gray-800 hover:bg-red-600 p-2 rounded-full transition-colors">
                  <FaYoutube className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-300 hover:text-white text-sm transition-colors">Home</Link></li>
                <li><Link to="/booking" className="text-gray-300 hover:text-white text-sm transition-colors">Book a Court</Link></li>
                <li><Link to="/membership" className="text-gray-300 hover:text-white text-sm transition-colors">Membership</Link></li>
                <li><Link to="/contact-payment" className="text-gray-300 hover:text-white text-sm transition-colors">Contact & Payment</Link></li>
              </ul>
            </div>

            {/* Facilities */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">Facilities</h4>
              <ul className="space-y-2">
                <li className="text-gray-300 text-sm">Premium Quality Court</li>
                <li className="text-gray-300 text-sm">Coaching Sessions</li>
                <li className="text-gray-300 text-sm">Professional Lighting</li>
                <li className="text-gray-300 text-sm">Changing Rooms</li>
                <li className="text-gray-300 text-sm">First Aid Facility</li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">Contact</h4>
              <ul className="space-y-2">
                <li>
                  <a href="https://wa.me/918080517517" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white text-sm transition-colors flex items-center">
                    <FaWhatsapp className="h-4 w-4 mr-2 text-green-500" />
                    +91 8080517517
                  </a>
                </li>
                <li>
                  <a href="mailto:playatbbc@gmail.com" className="text-gray-300 hover:text-white text-sm transition-colors">
                    playatbbc@gmail.com
                  </a>
                </li>
                <li>
                  <a href="https://www.google.com/maps?q=12.8628313,80.0860179" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white text-sm transition-colors">
                    Urapakkam, Chennai
                  </a>
                </li>
                <li className="text-gray-300 text-sm">5 AM – 9 PM | All Days</li>
              </ul>
            </div>

            {/* Location */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">Location</h4>
              <a
                href="https://www.google.com/maps?q=12.8628313,80.0860179"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white text-sm transition-colors leading-relaxed block"
              >
                Plot No.98, 2nd Street<br />
                Godavari Nagar, Iyyenchery<br />
                Urapakkam,<br />
                Chennai - 603 210.
              </a>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-gray-800 pt-6 flex flex-col md:flex-row justify-between items-center text-sm text-gray-500">
            <p>© 2025 - {new Date().getFullYear()} Best Badminton Club. All rights reserved.</p>
            <button
              onClick={() => setShowTerms(true)}
              className="mt-2 md:mt-0 text-green-500 hover:text-green-400 transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </footer>

      <TermsModal
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
      />
    </>
  );
}

export default Footer;
